import * as _ from "lodash";
import { Box, HStack, Stack, VStack } from "@chakra-ui/layout";
import { useEffect, useState } from "react";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import LoadingIndicator from "../components/Loading";
import {
	useCustomer,
	useCustomerAccountNotificationSettings,
	useCustomerUpdate,
	useCustomerUsersCustomers,
	useLoggedInUser,
} from "@api/hooks";
import { Text, Divider, InputRightElement, Button, useToast } from "@chakra-ui/react";
import SimpleSelect from "../components/ChakraUI/SimpleSelect";
import SimpleInput from "../components/ChakraUI/SimpleInput";
import { LockIcon } from "@chakra-ui/icons";
import SimpleTextArea from "../components/ChakraUI/SimpleTextArea";
import CheckCircleIcon from "../components/icons/CheckCircleIcon";
import EmptyWhiteCircle from "../components/icons/EmptyWhiteCircle";
import { getNotificationSettingList } from "@utils/customerUtils";

const WorkPlacePage = () => {
	const toast = useToast();
	const user = useLoggedInUser();
	const { customers, isLoading } = useCustomerUsersCustomers();

	const [selectedCustomer, setSelectedCustomer] = useState("");
	const [notificationSettings, setNotificationSettings] = useState<string[]>([]);
	const [workPlaceDescription, setWorkPlaceDescription] = useState("");

	const { customer, isLoading: isLoadingCustomer, refetch } = useCustomer(selectedCustomer);
	const { mutate: updateNotificationSettings, isLoading: isLoadingNotificationSettingsUpdate } =
		useCustomerAccountNotificationSettings(
			() => {
				toast({
					title: "Aviseringsinställningar information uppdaterad",
					status: "success",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			},
			() => {
				toast({
					title: "Aviseringsinställningar datauppdatering misslyckades",
					status: "error",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			},
		);

	const { mutate: updateCustomer, isLoading: isLoadingCustomerUpdate } = useCustomerUpdate(
		() => {
			toast({
				title: "Allmän information uppdaterad",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
		() => {
			toast({
				title: "Allmän datauppdatering misslyckades",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
	);

	const customerOptions = customers?.customers?.map((customer) => ({ key: customer.id, value: customer?.name || "" }));

	useEffect(() => {
		customerOptions && customerOptions.length > 0 && setSelectedCustomer(customerOptions[0].key);
	}, [JSON.stringify(customerOptions)]);

	useEffect(() => {
		customer && customer?.description && setWorkPlaceDescription(customer.description);
	}, [customer?.id]);

	useEffect(() => {
		if (selectedCustomer && customers?.customers) {
			const customerNotificationSettings = getNotificationSettingList(selectedCustomer, customers?.customers);
			setNotificationSettings(customerNotificationSettings);
		}
	}, [selectedCustomer, customers?.customers]);

	const handleSaveWorkPlace = async () => {
		if (customer && workPlaceDescription.trim() !== customer?.description) {
			await updateCustomer({ customerId: selectedCustomer, body: { description: workPlaceDescription } });
		}
		const customerNotificationSettings = getNotificationSettingList(selectedCustomer, customers?.customers);
		if (!_.isEqual(_.sortBy(customerNotificationSettings), _.sortBy(notificationSettings))) {
			await updateNotificationSettings({
				customerId: selectedCustomer,
				userId: user.id,
				body: {
					jobConfirmation: notificationSettings.includes("jobConfirmation") ? true : false,
					timeReportSummery: notificationSettings.includes("timeReportSummery") ? true : false,
					jobReport: notificationSettings.includes("jobReport") ? true : false,
				},
			});
		}
		refetch();
	};

	const isDisableSave = (): boolean => {
		const isDescriptionChanged = customer && workPlaceDescription.trim() !== customer?.description;
		const customerNotificationSettings = getNotificationSettingList(selectedCustomer, customers?.customers);
		if (!_.isEqual(_.sortBy(customerNotificationSettings), _.sortBy(notificationSettings)) || isDescriptionChanged)
			return false;
		return true;
	};

	const handleNotificationSettingCHange = (setting: string) => {
		if (notificationSettings.includes(setting)) {
			setNotificationSettings((prev) => prev.filter((ns) => ns !== setting));
		} else {
			setNotificationSettings((prev) => [...prev, setting]);
		}
	};

	const getAddress = () => {
		if (customer?.visitingAddress?.address1 && customer?.visitingAddress?.address2)
			return `${customer?.visitingAddress?.address1}, ${customer?.visitingAddress?.address2}`;
		if (customer?.visitingAddress?.address1) return `${customer?.visitingAddress?.address1}`;
		if (customer?.visitingAddress?.address2) return `${customer?.visitingAddress?.address2}`;
		return "";
	};

	return (
		<LoggedInLayout width="l" bg={"#edeff3"}>
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full">
				<Text fontWeight={600} fontSize={24} mt={2} mb={0}>
					Arbetsplats
				</Text>
				{isLoading && <LoadingIndicator size={"s"} />}
				{!isLoading && (
					<SimpleSelect
						fontSize={"16px"}
						width={250}
						value={selectedCustomer}
						onChange={(e) => setSelectedCustomer(e.target.value)}
						options={customerOptions}
					/>
				)}
				{!isLoading && (
					<Box borderRadius={"lg"} backgroundColor={"white"} p={8} my={3} w="full">
						{isLoadingCustomer ? (
							<LoadingIndicator size={"s"} />
						) : (
							<>
								<VStack spacing="5" alignItems={"start"} w="full">
									<VStack alignItems="start" w="full" spacing="1">
										<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
											Allmänt
										</Text>
										<Divider mt={2} />
									</VStack>
									<Stack
										direction={{ base: "column", xl: "row" }}
										alignItems="start"
										mr={5}
										minWidth={"100%"}
										spacing="6"
									>
										<SimpleInput
											width={"33%"}
											label="Adress"
											value={getAddress()}
											readOnly
											inputIcon={
												<InputRightElement pointerEvents="none">
													<LockIcon color="gray.400" />
												</InputRightElement>
											}
										/>
										<SimpleInput
											width={"33%"}
											label="Postnummer"
											value={customer?.visitingAddress?.postalCode || ""}
											readOnly
											inputIcon={
												<InputRightElement pointerEvents="none">
													<LockIcon color="gray.400" />
												</InputRightElement>
											}
										/>
										<SimpleInput
											width={"33%"}
											label="Ort"
											value={customer?.visitingAddress?.city || ""}
											readOnly
											inputIcon={
												<InputRightElement pointerEvents="none">
													<LockIcon color="gray.400" />
												</InputRightElement>
											}
										/>
									</Stack>
									<SimpleTextArea
										width={"100%"}
										labelFontSize="14px"
										labelFontWeight={700}
										placeHolder="Ange praktisk information om arbetsplatsen, t.ex. mötesplats, ev. förbud, att tänka på, önskemål om klädsel, m.m."
										label="Om arbetsplatsen"
										value={workPlaceDescription}
										onChange={(e) => setWorkPlaceDescription(e.target.value)}
									/>
								</VStack>
								<VStack spacing="5" alignItems={"start"} w="full" my={7}>
									<VStack alignItems="start" w="full" spacing="1">
										<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
											Aviseringsinställningar
										</Text>
										<Divider mt={2} />
									</VStack>
									<VStack spacing={"5"} alignItems="start">
										{[
											{ label: "Orderbekfräftelse (e-post)", keyValue: "jobConfirmation" },
											{ label: "Konsultrapporter (e-post)", keyValue: "jobReport" },
											{ label: "Tidrapporter (e-post)", keyValue: "timeReportSummery" },
										].map((item) => (
											<HStack
												key={item.keyValue}
												cursor={"pointer"}
												onClick={() => handleNotificationSettingCHange(item.keyValue)}
												spacing="2.5"
											>
												{notificationSettings.includes(item.keyValue) ? <CheckCircleIcon /> : <EmptyWhiteCircle />}
												<Text fontWeight={500} fontSize={16} my={0} color={"#333333"}>
													{item.label}
												</Text>
											</HStack>
										))}
									</VStack>
									<Divider mt={5} />
									<Button
										variant="solid"
										height="40px"
										borderRadius={"20px"}
										width={"240px"}
										color="white"
										bg={"#2A2A2A"}
										mx={"auto"}
										_hover={{
											backgroundColor: isDisableSave() ? "#9fa0a3" : "#2A2A2A",
										}}
										onClick={handleSaveWorkPlace}
										isDisabled={isDisableSave()}
										isLoading={isLoadingCustomerUpdate || isLoadingNotificationSettingsUpdate}
									>
										Spara ändringar
									</Button>
								</VStack>
							</>
						)}
					</Box>
				)}
			</VStack>
		</LoggedInLayout>
	);
};

export default WorkPlacePage;
